import {createSelector} from 'reselect';
import {doesViewableHaveSheetInfo, getLinkedDocument, isSameReferredSheet} from '../../utils/issues-helpers';
import {isErrorLoadingIssueTypesAdminList, isLoadingIssueTypesAdminList} from './issueTypes-selectors';
import {
  isErrorLoadingIssueAttributesDefinitions,
  isErrorLoadingIssueAttributesMappings,
  isLoadingIssueAttributesDefinitions,
  isLoadingIssueAttributesMappings
} from './issueAttributes-selectors';
import {getConfiguration} from './configuration-selectors';
import {isLoadingIssueRootCausesDefinitions} from './issueRootCauses-selectors';
import {isLoadingRequiredFields} from './requiredfields-selectors';

const getIssuesCollectionWrapper = state => state.issues;

export const isIssuesBatchLoading = createSelector(
  getIssuesCollectionWrapper,
  ({batchLoading}) => batchLoading
);

export const getIssuesCollectionDidFetchOnce = createSelector(
  getIssuesCollectionWrapper,
  ({didFetchOnce}) => didFetchOnce
);

export const isIssueMetadataLoadingError = createSelector(
  isErrorLoadingIssueTypesAdminList,
  isErrorLoadingIssueAttributesDefinitions,
  isErrorLoadingIssueAttributesMappings,
  (errorIssueTypes, errorAttributeDefinitions, errorAttributeMappings) =>
    errorIssueTypes || errorAttributeDefinitions || errorAttributeMappings
);

export const getIssuesCollectionError = createSelector(
  getIssuesCollectionWrapper,
  isIssueMetadataLoadingError,
  ({error}, fieldMandatoryFieldsError) => error || fieldMandatoryFieldsError
);

export const getIssuesCollection = createSelector(
  getIssuesCollectionWrapper,
  issues => issues.collection
);

export const getIssuesCollectionAttributes = createSelector(
  getIssuesCollectionWrapper,
  state => state, // pass-through 2nd argument from higher-order/composite selector
  ({collection}) => {
    const result = Object.keys(collection).map(id => Object.assign({id}, {...collection[id].attributes}));
    return result;
  }
);

export const getIssuesCollectionPagination = createSelector(
  getIssuesCollectionWrapper,
  ({pagination}) => pagination
);

export const getIssuesCollectionSearchResults = createSelector(
  getIssuesCollectionWrapper,
  ({searchResults}) => searchResults
);

export const getIssuesOrder = createSelector(
  getIssuesCollectionWrapper,
  ({sortOrder, collection}) => sortOrder.filter(id => collection[id])
);

const EMPTY_OBJECT = Object.freeze({});

export const getIssueAttributesById = createSelector(
  getIssuesCollection,
  (state, issueId) => issueId,
  (collection, issueId) => (collection[issueId] ? collection[issueId].attributes : EMPTY_OBJECT)
);

export const getAllIssueWrappersSorted = createSelector(
  getIssuesCollectionWrapper,
  ({sortOrder, collection}) => sortOrder.map(id => collection[id]).filter(issue => !!issue)
);

export const getSortedIssuesFilteredBySheetsInDocument = createSelector(
  getConfiguration,
  getAllIssueWrappersSorted,
  state => state,
  ({sheetsConfig}, issues, {filterCallback}) => {
    if (typeof filterCallback === 'function') {
      // Call the optional filter callback passed by the hosting app. The client could for example
      // filter all issues by objectId.
      issues = issues.filter(({attributes: issue}) => filterCallback(issue));
    }

    // Here it filters out the issues not in currentSheet, nor in sheetList
    return issues.filter(({attributes: issue}) => {
      const viewable = getLinkedDocument(issue)?.details?.viewable;
      return (
        !viewable ||
        // It seems that no currentSheet means: Show all
        !sheetsConfig.currentSheets.length ||
        !doesViewableHaveSheetInfo(viewable) ||
        sheetsConfig.sheetList.some(sheet => isSameReferredSheet(sheet, viewable))
      );
    });
  }
);

export const getFieldIssueMandatoryDataLoading = createSelector(
  isLoadingIssueTypesAdminList,
  isLoadingIssueRootCausesDefinitions,
  isLoadingIssueAttributesDefinitions,
  isLoadingIssueAttributesMappings,
  isLoadingRequiredFields,
  (
    fieldIssueTypesLoading,
    fieldIssuesRootCausesLoading,
    issueAttributeDefinitionsLoading,
    issueAttributeMappingsLoading,
    requiredFieldsLoading
  ) =>
    fieldIssueTypesLoading ||
    fieldIssuesRootCausesLoading ||
    issueAttributeDefinitionsLoading ||
    issueAttributeMappingsLoading ||
    requiredFieldsLoading
);

export const getIssuesCollectionLoading = createSelector(
  isIssuesBatchLoading,
  getFieldIssueMandatoryDataLoading,
  (isBatchLoading, fieldIssueMandatoryDataLoading) => fieldIssueMandatoryDataLoading || isBatchLoading
);

export const getLastQueryKey = (state, type) => state.issues.lastQueryKey[type];

// Returns total number of issues for current filters
export const getCurrentIssuesCount = createSelector(
  getIssuesCollectionPagination,
  pagination => pagination?.totalResults || 0
);
